import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from "./components/ui/toaster";
import { Toaster as SonnerToaster } from "sonner";
import { TooltipProvider } from "./components/ui/tooltip";
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import AppInitializer from './components/AppInitializer';
import RouteProtector from './components/RouteProtector';
import { AuthGuard } from './components/AuthGuard';
import Navigation from './components/Navigation';

// Public pages - loaded immediately
import Index from './pages/Index';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Pricing from './pages/Pricing';
import ContactUs from './pages/ContactUs';
import HelpCenter from './pages/HelpCenter';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import NotFound from './pages/NotFound';
import AccountSetup from './pages/AccountSetup';
import AuthConfirm from './pages/AuthConfirm';

// Lazy loaded protected pages - only loaded when needed
const Dashboard = lazy(() => import('./pages/Dashboard'));
const LandlordDashboard = lazy(() => import('./pages/LandlordDashboard'));
const Discover = lazy(() => import('./pages/Discover'));
const EnhancedDiscover = lazy(() => import('./pages/EnhancedDiscover'));
const Matches = lazy(() => import('./pages/Matches'));
const Messages = lazy(() => import('./pages/Messages'));
const Chat = lazy(() => import('./pages/Chat'));
const Profile = lazy(() => import('./pages/Profile'));
const Settings = lazy(() => import('./pages/Settings'));
const Notifications = lazy(() => import('./pages/Notifications'));

// Lazy loaded property pages
const AddListing = lazy(() => import('./pages/AddListing'));
const PropertyManagementPage = lazy(() => import('./components/landlord/PropertyManagementPage'));
const PropertyDetails = lazy(() => import('./pages/PropertyDetails'));
const ApplicationPage = lazy(() => import('./pages/ApplicationPage'));
const MyApplication = lazy(() => import('./pages/MyApplication'));
const ApplyToProperty = lazy(() => import('./pages/ApplyToProperty'));
const ApplicationConfirmation = lazy(() => import('./pages/ApplicationConfirmation'));
const ScheduleTour = lazy(() => import('./pages/ScheduleTour'));

// Lazy loaded landlord pages
const LandlordMessages = lazy(() => import('./pages/LandlordMessages'));
const LandlordTours = lazy(() => import('./pages/LandlordTours'));
const LandlordLeases = lazy(() => import('./pages/LandlordLeases'));
const LandlordAnalytics = lazy(() => import('./pages/LandlordAnalytics'));
const LandlordFormsHub = lazy(() => import('./pages/LandlordFormsHub'));

// Lazy loaded utility pages
const RenterTimeline = lazy(() => import('./pages/RenterTimeline'));
const RentalPriceGuide = lazy(() => import('./pages/RentalPriceGuide'));

// Map page with retry logic
const MapPage = lazy(() => 
  import('./pages/Map').catch(() => {
    window.location.href = window.location.href;
    return new Promise(() => {});
  })
);

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
      <p className="text-muted-foreground">Loading...</p>
    </div>
  </div>
);

function App() {
  return (
    <ThemeProvider>
      <TooltipProvider delayDuration={0}>
        <AuthProvider>
          <Router>
            <AppInitializer>
              <div className="min-h-screen bg-background font-sans antialiased">
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/auth/confirm" element={<AuthConfirm />} />
                <Route path="/login" element={
                  <AuthGuard requireAuth={false}>
                    <Login />
                  </AuthGuard>
                } />
                <Route path="/signup" element={
                  <AuthGuard requireAuth={false}>
                    <Signup />
                  </AuthGuard>
                } />
                <Route path="/forgot-password" element={
                  <AuthGuard requireAuth={false}>
                    <ForgotPassword />
                  </AuthGuard>
                } />
                <Route path="/reset-password" element={
                  <AuthGuard requireAuth={false}>
                    <ResetPassword />
                  </AuthGuard>
                } />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/account-setup" element={<AccountSetup />} />
                <Route path="/contact-us" element={<ContactUs />} />
                <Route path="/help" element={<HelpCenter />} />
                <Route path="/help/finding-properties" element={React.createElement(React.lazy(() => import('./pages/help/FindingProperties')))} />
                <Route path="/help/communication" element={React.createElement(React.lazy(() => import('./pages/help/Communication')))} />
                <Route path="/help/landlords" element={React.createElement(React.lazy(() => import('./pages/help/ForLandlords')))} />
                <Route path="/help/account" element={React.createElement(React.lazy(() => import('./pages/help/AccountManagement')))} />
                <Route path="/help/billing" element={React.createElement(React.lazy(() => import('./pages/help/BillingPricing')))} />
                <Route path="/help/safety" element={React.createElement(React.lazy(() => import('./pages/help/SafetySecurity')))} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />

                {/* Protected routes */}
                <Route path="/dashboard" element={
                  <AuthGuard allowedUserTypes={['renter']}>
                    <RouteProtector>
                      <Suspense fallback={<PageLoader />}>
                        <Dashboard />
                      </Suspense>
                    </RouteProtector>
                  </AuthGuard>
                } />
                <Route path="/landlord-dashboard" element={
                  <AuthGuard allowedUserTypes={['landlord']}>
                    <RouteProtector>
                      <Suspense fallback={<PageLoader />}>
                        <LandlordDashboard />
                      </Suspense>
                    </RouteProtector>
                  </AuthGuard>
                } />
                <Route path="/discover" element={
                  <AuthGuard>
                    <RouteProtector>
                      <Suspense fallback={<PageLoader />}>
                        <Discover />
                      </Suspense>
                    </RouteProtector>
                  </AuthGuard>
                } />
                <Route path="/enhanced-discover" element={
                  <AuthGuard>
                    <RouteProtector>
                      <Suspense fallback={<PageLoader />}>
                        <EnhancedDiscover />
                      </Suspense>
                    </RouteProtector>
                  </AuthGuard>
                } />
                <Route path="/matches" element={
                  <AuthGuard>
                    <RouteProtector>
                      <Suspense fallback={<PageLoader />}>
                        <Matches />
                      </Suspense>
                    </RouteProtector>
                  </AuthGuard>
                } />
                <Route path="/messages" element={
                  <AuthGuard>
                    <RouteProtector>
                      <Suspense fallback={<PageLoader />}>
                        <Messages />
                      </Suspense>
                    </RouteProtector>
                  </AuthGuard>
                } />
                <Route path="/chat/:conversationId?" element={
                  <AuthGuard>
                    <RouteProtector>
                      <Suspense fallback={<PageLoader />}>
                        <Chat />
                      </Suspense>
                    </RouteProtector>
                  </AuthGuard>
                } />
                <Route path="/profile" element={
                  <AuthGuard>
                    <RouteProtector>
                      <Suspense fallback={<PageLoader />}>
                        <Profile />
                      </Suspense>
                    </RouteProtector>
                  </AuthGuard>
                } />
                <Route path="/settings" element={
                  <AuthGuard>
                    <RouteProtector>
                      <Suspense fallback={<PageLoader />}>
                        <Settings />
                      </Suspense>
                    </RouteProtector>
                  </AuthGuard>
                } />
                <Route path="/notifications" element={
                  <AuthGuard>
                    <RouteProtector>
                      <Suspense fallback={<PageLoader />}>
                        <Notifications />
                      </Suspense>
                    </RouteProtector>
                  </AuthGuard>
                } />

                {/* Property routes */}
                <Route path="/add-listing" element={
                  <RouteProtector>
                    <Suspense fallback={<PageLoader />}>
                      <AddListing />
                    </Suspense>
                  </RouteProtector>
                } />
                <Route path="/property/:propertyId" element={
                  <RouteProtector>
                    <Suspense fallback={<PageLoader />}>
                      <PropertyManagementPage />
                    </Suspense>
                  </RouteProtector>
                } />
                <Route path="/property-details/:propertyId" element={
                  <RouteProtector>
                    <Suspense fallback={<PageLoader />}>
                      <PropertyDetails />
                    </Suspense>
                  </RouteProtector>
                } />
                <Route path="/property-management/:propertyId" element={
                  <RouteProtector>
                    <Suspense fallback={<PageLoader />}>
                      <PropertyManagementPage />
                    </Suspense>
                  </RouteProtector>
                } />
                <Route path="/application/:propertyId" element={
                  <RouteProtector>
                    <Suspense fallback={<PageLoader />}>
                      <ApplicationPage />
                    </Suspense>
                  </RouteProtector>
                } />
                <Route path="/my-application" element={
                  <RouteProtector>
                    <Suspense fallback={<PageLoader />}>
                      <MyApplication />
                    </Suspense>
                  </RouteProtector>
                } />
                <Route path="/apply" element={
                  <RouteProtector>
                    <Suspense fallback={<PageLoader />}>
                      <ApplyToProperty />
                    </Suspense>
                  </RouteProtector>
                } />
                <Route path="/application-confirmation" element={
                  <RouteProtector>
                    <Suspense fallback={<PageLoader />}>
                      <ApplicationConfirmation />
                    </Suspense>
                  </RouteProtector>
                } />
                <Route path="/schedule-tour/:propertyId" element={
                  <RouteProtector>
                    <Suspense fallback={<PageLoader />}>
                      <ScheduleTour />
                    </Suspense>
                  </RouteProtector>
                } />

                {/* Landlord specific routes */}
                <Route path="/landlord-messages" element={
                  <AuthGuard allowedUserTypes={['landlord']}>
                    <RouteProtector>
                      <Suspense fallback={<PageLoader />}>
                        <LandlordMessages />
                      </Suspense>
                    </RouteProtector>
                  </AuthGuard>
                } />
                <Route path="/landlord-tours" element={
                  <AuthGuard allowedUserTypes={['landlord']}>
                    <RouteProtector>
                      <Suspense fallback={<PageLoader />}>
                        <LandlordTours />
                      </Suspense>
                    </RouteProtector>
                  </AuthGuard>
                } />
                <Route path="/landlord-leases" element={
                  <AuthGuard allowedUserTypes={['landlord']}>
                    <RouteProtector>
                      <Suspense fallback={<PageLoader />}>
                        <LandlordLeases />
                      </Suspense>
                    </RouteProtector>
                  </AuthGuard>
                } />
                <Route path="/landlord-analytics" element={
                  <AuthGuard allowedUserTypes={['landlord']}>
                    <RouteProtector>
                      <Suspense fallback={<PageLoader />}>
                        <LandlordAnalytics />
                      </Suspense>
                    </RouteProtector>
                  </AuthGuard>
                } />
                <Route path="/landlord-forms" element={
                  <AuthGuard allowedUserTypes={['landlord']}>
                    <RouteProtector>
                      <Suspense fallback={<PageLoader />}>
                        <LandlordFormsHub />
                      </Suspense>
                    </RouteProtector>
                  </AuthGuard>
                } />

                {/* Utility routes */}
                <Route path="/map" element={
                  <RouteProtector>
                    <Suspense fallback={<PageLoader />}>
                      <MapPage key={window.location.pathname} />
                    </Suspense>
                  </RouteProtector>
                } />
                <Route path="/timeline" element={
                  <RouteProtector>
                    <Suspense fallback={<PageLoader />}>
                      <RenterTimeline />
                    </Suspense>
                  </RouteProtector>
                } />
                <Route path="/rental-guide" element={
                  <RouteProtector>
                    <Suspense fallback={<PageLoader />}>
                      <RentalPriceGuide />
                    </Suspense>
                  </RouteProtector>
                } />

                {/* 404 route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              <Navigation />
              </div>
            </AppInitializer>
            <Toaster />
            <SonnerToaster />
          </Router>
        </AuthProvider>
      </TooltipProvider>
    </ThemeProvider>
  );
}

export default App;
